import router from "../../router";
import ApiService from "@services/api.service";
import TokenService from "@services/token.service";
import StorageService from "@services/storage.service";
import { ROLES } from "@/config/permissions";

export default {
  namespaced: true,

  state: {
    status: "",
    token: TokenService.getToken() || "",
    user: StorageService.getItem("user", true, true) || {},
    role: StorageService.getItem("role", false, true) || null,
    roles: StorageService.getItem("roles", true, true) || {},
    permissions: StorageService.getItem("permissions", true, true) || {},
    intendedRoute: null,
  },

  getters: {
    isLoggedIn: (state) => !!state.token,
    authStatus: (state) => state.status,
    currentUser: (state) => state.user,
    currentUserRole: (state) => state.role,
    currentUserPermissions: (state) => state.permissions,
    intendedRoute: (state) => state.intendedRoute,
  },

  actions: {
    enterDashboard({ state, getters }) {
      switch (state.role) {
        case ROLES.TEACHERS:
          if (
            ["/login", "/select-role"].includes(router.currentRoute.path) ||
            router.currentRoute.path == "/"
          ) {
            return router.push("/teacher-dashboard");
          }

          return;
        case ROLES.HOD:
          return router.push("/hod-dashboard");

        case ROLES.SCHOOLADMINS:
          return router.push("/");

        case ROLES.SYSTEMADMINS:
          if (["/login"].includes(router.currentRoute.path)) {
            if (state.user.school) {
              return router.push("/");
            }
            return router.push("/super-admin/dashboard");
          }

        default:
          if (getters.isLoggedIn && router.currentRoute.path == "/login") {
            router.push(router.currentRoute.query.redirect || "/");
          }
      }
    },

    socialLogin({ commit }, user) {
      return new Promise((resolve, reject) => {
        commit("auth_request");
        commit("setSuperadminLoggedSchool", null, {
          root: true,
        });
        commit("setDistrictLoggedSchool", null, {
          root: true,
        });

        ApiService.post("/social/login", user)
          .then((resp) => {
            const data = resp.data;

            StorageService.removeItem("superadminLoggedSchool");

            // Save token to storage
            TokenService.saveToken(data.access_token);

            // Refresh header with new token
            ApiService.setAuthHeader();

            // Save user, roles, permissions to storage
            StorageService.saveItem("user", data.user);
            StorageService.saveItem("roles", data.roles);
            StorageService.saveItem("permissions", data.permissions);

            commit("auth_success");

            resolve(resp);
          })
          .catch((err) => {
            commit("auth_error");

            // Remove user from storage
            StorageService.removeItem("user");
            StorageService.clean();

            // Remove token from local storage
            TokenService.removeToken();

            reject(err);
          });
      });
    },

    login({ commit }, user) {
      return new Promise((resolve, reject) => {
        commit("auth_request");
        commit("setSuperadminLoggedSchool", null, {
          root: true,
        });
        commit("setDistrictLoggedSchool", null, {
          root: true,
        });

        ApiService.post("/login", user)
          .then((resp) => {
            const data = resp.data;

            StorageService.removeItem("superadminLoggedSchool");

            // Save token to storage
            TokenService.saveToken(data.access_token);

            // Refresh header with new token
            ApiService.setAuthHeader();

            // Save user, roles, permissions to storage
            StorageService.saveItem("user", data.user);
            StorageService.saveItem("roles", data.roles);
            StorageService.saveItem("permissions", data.permissions);

            commit("auth_success");

            resolve(resp);
          })
          .catch((err) => {
            commit("auth_error");

            // Remove user from storage
            StorageService.removeItem("user");
            StorageService.clean();

            // Remove token from local storage
            TokenService.removeToken();

            reject(err);
          });
      });
    },

    register({ commit }, user) {
      return new Promise((resolve, reject) => {
        commit("auth_request");

        ApiService.post("/register", user)
          .then((resp) => {
            const data = resp.data;

            // Save token to storage
            TokenService.saveToken(data.access_token);

            // Refresh header with new token
            ApiService.setAuthHeader();

            // Save user to storage
            StorageService.saveItem("user", data.user);

            commit("auth_success", data);
            resolve(resp);
          })
          .catch((err) => {
            commit("auth_error", err);

            // Remove user from storage
            StorageService.removeItem("user");

            // Remove token from local storage
            TokenService.removeToken();

            reject(err);
          });
      });
    },

    logout({ commit }) {
      // Logout from server
      return new Promise((resolve, reject) => {
        commit("logout");

        ApiService.post("/logout")
          .then((res) => {
            resolve(res);
          })
          .catch((err) => {
            reject(err);
          })
          .finally(() => {
            // Save current locale before clearing storage
            const currentLocale = localStorage.getItem("locale");

            // Remove user from storage
            StorageService.removeItem("user");
            StorageService.removeItem("role");
            StorageService.removeItem("roles");
            StorageService.removeItem("permissions");
            StorageService.removeItem("superadminLoggedSchool");
            StorageService.removeItem("districtLoggedSchool");

            // Clear storage but preserve locale
            StorageService.clean();

            // Restore locale if it existed
            if (currentLocale) {
              localStorage.setItem("locale", currentLocale);
            }

            // Remove token from storage
            TokenService.removeToken();

            // Remove token from header
            ApiService.removeAuthHeader();

            // Redirect to login view
            router.push("/landing");
          });
      });
    },
  },

  mutations: {
    auth_request(state) {
      state.status = "loading";
    },

    auth_success(state) {
      state.status = "success";
      state.token = TokenService.getToken();
      state.user = StorageService.getItem("user", true, true);
      state.role = StorageService.getItem("role", false, true);
      state.roles = StorageService.getItem("roles", true, true);
      state.permissions = StorageService.getItem("permissions", true, true);
    },

    auth_error(state) {
      state.status = "error";
    },
    set_user(state, user) {
      state.user = user;
    },

    logout(state) {
      state.status = "";
      state.token = "";
      state.user = {};
      state.role = null;
      state.roles = {};
      state.permissions = {};
    },

    set_school(state, newSchool) {
      state.user.school = newSchool;
    },
    setIntendedRoute(state, route) {
      state.intendedRoute = route;
    },
    clearIntendedRoute(state) {
      state.intendedRoute = null;
    },
  },
};
